import { useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";
import RepoVerhuizen from "./RepoVerhuizen";

/**
 * EG9 (GO Frank 1 november 2026): een repo importeren uit een map op deze Mac of van GitHub.
 *
 * Naar "Import a repository" op GitHub: eerst waar hij vandaan komt, dan de naam (mag leeg: dan de naam van de map of
 * de repo). Bij GitHub per repo de keuze wie de bron is: GitHub (een spiegel, in NovaMund alleen te lezen) of NovaMund
 * (eenmalig importeren). G1: een privé-repo met een leestoken dat Frank zelf maakt; het gaat alleen met dit ene verzoek
 * mee, staat nergens in het scherm bewaard (het veld wordt na het verzoek leeg) en de backend bewaart het niet.
 */
export default function RepoImporteren({ onKlaar }) {
  const { t } = useTaal();
  const [soort, setSoort] = useState("github");
  const [adres, setAdres] = useState("");
  const [pad, setPad] = useState("");
  const [spiegelen, setSpiegelen] = useState(true);
  const [naam, setNaam] = useState("");
  const [token, setToken] = useState("");
  const [bezig, setBezig] = useState(false);
  const leeg = soort === "github" ? !adres.trim() : !pad.trim();
  // CA6: "bundel" gebruikt hetzelfde padveld als een map.

  async function importeer(e) {
    e.preventDefault();
    setBezig(true);
    try {
      const { data } = await axios.post(`${API}/repos/importeer`, {
        soort, adres: adres.trim(), pad: pad.trim(), spiegelen: soort === "github" && spiegelen, name: naam.trim(),
        token: soort === "github" ? token.trim() : "",
      });
      toast.success(t("Repo {naam} geïmporteerd.", { naam: data.name }));
      // Wat in de map nog niet was vastgelegd, ging niet mee; dat hoort Frank te weten.
      if (data.bron?.niet_vastgelegd) {
        toast.warning(t("{n} bestanden in de map waren nog niet vastgelegd en gingen niet mee.", { n: data.bron.niet_vastgelegd }));
      }
      onKlaar?.(data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? t(detail) : t("Importeren lukte niet."));
    } finally {
      setToken("");
      setBezig(false);
    }
  }

  return (
    <form onSubmit={importeer} className="space-y-2 rounded-lg border p-3" data-testid="repo-importeren">
      <div className="flex flex-wrap gap-3 text-sm" role="radiogroup" aria-label={t("Waar komt de repo vandaan")}>
        {[["github", t("Van GitHub")], ["map", t("Uit een map op deze Mac")], ["bundel", t("Uit een bundel op deze Mac")],
          ["verhuis", t("Verhuizen uit een git-map, met alles")]].map(([id, label]) => (
          <label key={id} className="flex items-center gap-1">
            <input type="radio" name="importsoort" checked={soort === id} onChange={() => setSoort(id)} data-testid={`import-${id}`} />
            {label}
          </label>
        ))}
      </div>
      {/* EV1 (besluit Frank 2 nov): verhuizen heeft een eigen kaart en Bevestig, per repo, en nooit in een klik. */}
      {soort === "verhuis" ? <RepoVerhuizen onKlaar={onKlaar} /> : (<>
      {soort === "github" ? (
        <>
          <Input value={adres} onChange={(e) => setAdres(e.target.value)} placeholder="https://github.com/eigenaar/naam"
                 aria-label={t("Adres op GitHub")} data-testid="import-adres" />
          <fieldset className="space-y-1 text-sm">
            <legend className="text-xs text-muted-foreground">{t("Wie is de bron?")}</legend>
            <label className="flex items-start gap-2">
              <input type="radio" name="importbron" checked={spiegelen} onChange={() => setSpiegelen(true)} data-testid="import-spiegelen" />
              <span>{t("GitHub blijft de bron: NovaMund spiegelt hem en je leest hem hier, je werkt er niet in.")}</span>
            </label>
            <label className="flex items-start gap-2">
              <input type="radio" name="importbron" checked={!spiegelen} onChange={() => setSpiegelen(false)} data-testid="import-eenmalig" />
              <span>{t("Eenmalig importeren: daarna is NovaMund de bron.")}</span>
            </label>
          </fieldset>
          <Input type="password" autoComplete="off" value={token} onChange={(e) => setToken(e.target.value)}
                 placeholder={t("Leestoken van GitHub (alleen voor een privé-repo)")}
                 aria-label={t("Leestoken van GitHub (alleen voor een privé-repo)")} data-testid="import-token" />
          <p className="text-xs text-muted-foreground">
            {t("NovaMund gebruikt geen inlog van GitHub. Een privé-repo vraagt een leestoken dat je zelf op GitHub maakt; het gaat alleen met deze import mee en wordt nergens bewaard.")}
          </p>
        </>
      ) : (
        <>
          <Input value={pad} onChange={(e) => setPad(e.target.value)}
                 placeholder={soort === "bundel" ? "/Users/naam/project.bundle" : "/Users/naam/project"}
                 aria-label={soort === "bundel" ? t("Volledig pad van de bundel") : t("Volledig pad van de map")} data-testid="import-pad" />
          <p className="text-xs text-muted-foreground">
            {soort === "bundel"
              ? t("Een bundel komt mee met al zijn takken en geschiedenis. Het bestand zelf wordt alleen gelezen.")
              : t("Een git-map komt mee met al zijn takken en geschiedenis; anders worden de bestanden de eerste vastlegging op main. De map zelf wordt alleen gelezen.")}
          </p>
        </>
      )}
      <Input value={naam} onChange={(e) => setNaam(e.target.value)} placeholder={t("Naam van de repo (mag leeg)")}
             aria-label={t("Naam van de repo (mag leeg)")} data-testid="import-naam" />
      <Button type="submit" className="tap-target" disabled={bezig || leeg} data-testid="import-start">
        {bezig && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}{t("Importeren")}
      </Button>
      </>)}
    </form>
  );
}
